import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {isEmpty} from 'lodash';
import {useHistory} from 'src/rerouter';
import type {JOB_INFORMATION} from 'src/types/crm';

import {useDispatch} from 'react-redux';
import * as reduxApi from 'src/utils/redux-api-v2';

import {getJobApprovalStatuses} from 'src/action-creators/crm';

import {
  SubTitleLarge,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import User from 'src/components/crm/lib/user/user';
import {formatMomentDateAndTime, formatDateTime} from 'src/utils/date-time';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {JobDetailsTab} from 'src/components/crm/lib/job-details-tab/job-details-tab';
import {JobDescription} from 'src/components/crm/lib/job-description/job-description';
import {ApprovalStatus} from './tabs/approval-status';
import css from './approve-job-form.css';


export const ApproveJobForm = ({
  isOpen,
  handleClose,
  handleMoveBack,
  currentJobId,
  currentApprovalId,
  isRejected = false,
  isApproved = false,
  onSubmit,
}: {
  isOpen: boolean;
  handleClose: (queryObject?: {readonly [key: string]: string}) => void;
  handleMoveBack: (refetch: boolean, removeJobId?: boolean) => void;
  currentJobId?: string;
  currentApprovalId?: string;
  isRejected?: boolean;
  isApproved?: boolean;
  onSubmit?: () => void;
}) => {
  const history = useHistory();
  const {location} = history;
  const dispatch = useDispatch();
  const {t, language} = useLabelTranslation();

  const approvalId: string = location.query.approvalId ?? currentApprovalId;
  const jobId = location.query.jobId || currentJobId || '';
  const [jobDetails, setJobDetails] = React.useState<JOB_INFORMATION | null>(
    null,
  );
  const [isLoadingJobData, setIsLoadingJobData] = React.useState(false);
  const [selectedOption, setSelectedTabOption] = React.useState({
    label: t('Job_Details', 'Job Details'),
    tabId: 'jobDetails',
  });
  const [modalState, setModalState] = React.useState<
    'approve' | 'reject' | null
  >(null);
  const [jobApprovalMessage, setJobApprovalMessage] = React.useState('');
  const [approvingJob, setApprovingJob] = React.useState(false);
  const [jobApprovalObject, setJobApprovalObject] = React.useState({});
  const [approverStatusesList, setApproverStatusesList] = React.useState([]);

  const tabComponents = {
    jobDetails: JobDetailsTab,
    jobDescription: JobDescription,
    approvalStatus: ApprovalStatus,
  } as const;

  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly jobDetails: ({ jobDetails, hideCustomInfo, }: { jobDetails: JOB_INFORMATION; hideCustomInfo?: boolean | undefined; }) => Element; readonly jobDescription: ({ jobInfo, hideExternalJD, }: { jobInfo: JOB_INFORMATION; hideExternalJD?: boolean | undefined; }) => Element; readonly approvalStatus: ({ approvalObj...'.
  const SelectedTabComponent = tabComponents[selectedOption.tabId] || null;

  const closePanelHandler = () => {
    handleClose();
  };

  const handleApprovalAction = async (
    status: string,
    messages: {
      errorMessage: string;
      successMessage: string;
    },
  ) => {
    setApprovingJob(true);
    const payload = {status, comment: jobApprovalMessage} as const;
    try {
      await dispatch(
        reduxApi.post(
          `api/job-approval-statuses/${approvalId}`,
          payload,
          {},
          {apiPath: '/trmapi/'},
        ),
      );
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>{messages.successMessage}</ToastTitle>
        </Toast>,
        {timeout: 2000},
      );
      if (onSubmit) {
        onSubmit();
      }
      handleMoveBack(true, true);
    } catch (error: any) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>{messages.errorMessage}</ToastTitle>
        </Toast>,
        {timeout: 2000},
      );
    } finally {
      setApprovingJob(false);
    }
  };

  React.useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setIsLoadingJobData(true);

        // Run all API calls simultaneously
        const [response, approvals, approvalStatusList] = await Promise.all([
          dispatch(
            reduxApi.get(`api/jobs/${jobId}`, {}, {apiPath: `/trmapi/`}),
          ),
          dispatch(
            reduxApi.get(
              `api/job-approval-chains/${jobId}`,
              {},
              {apiPath: `/trmapi/`},
            ),
          ),
          dispatch(getJobApprovalStatuses(jobId)),
        ]);

        const jobApproval = approvals.find(
          // @ts-expect-error - TS7006 - Parameter 'approval' implicitly has an 'any' type.
          (approval) => approval.type === 'JOB',
        );

        setJobDetails(response);
        setJobApprovalObject(jobApproval);
        // @ts-expect-error - TS2345 - Argument of type 'ThunkAction<void>' is not assignable to parameter of type 'SetStateAction<never[]>'.
        setApproverStatusesList(approvalStatusList);
      } catch (e: any) {
        console.error(e);
      } finally {
        setIsLoadingJobData(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const renderFooter = () => {
    if (isRejected || isApproved) {
      return null;
    }

    return (
      <PanelFooter>
        <div className={css.row}>
          <Button
            classNames={{wrapper: css.rejectButton}}
            onClick={() => setModalState('reject')}
          >
            {t('Reject', 'Reject')}
          </Button>
          <Button onClick={() => setModalState('approve')}>
            {t('Approve', 'Approve')}
          </Button>
        </div>
      </PanelFooter>
    );
  };
  const {user} = jobDetails ?? {};
  const renderStatusAlert = () => {
    if (isRejected) {
      return (
        // @ts-expect-error TS2769
        <InContextAlert leftIconName="shield-exclamation" semantic="danger">
          {t('Rejected_by', 'Rejected by')}:{' '}
          {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'updatedBy' does not exist on type '{}'. */}
          <User userId={parseInt(jobApprovalObject.updatedBy)} />
          {`${t('on', 'on')} `}
          {/* @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | undefined'. */}
          {formatMomentDateAndTime(jobDetails?.updatedOn, language)}
        </InContextAlert>
      );
    }

    if (isApproved) {
      return (
        // @ts-expect-error TS2769
        <InContextAlert leftIconName="circle-check" semantic="success">
          {t('Approved_by', 'Approved by')}:{' '}
          {/* @ts-expect-error - TS2769 - No overload matches this call. | TS2339 - Property 'updatedBy' does not exist on type '{}'. */}
          <User userId={parseInt(jobApprovalObject.updatedBy)} />,
          {t('on', 'on')}{' '}
          {/* @ts-expect-error - TS2345 - Argument of type 'number | undefined' is not assignable to parameter of type 'string | undefined'. */}
          {formatMomentDateAndTime(jobDetails?.updatedOn, language)}
        </InContextAlert>
      );
    }

    return (
      <div className={css.taskRow}>
        <Icon name="info-circle" color="secondary" />
        <BodyMedium color="secondary">
          {`${t(
            'Job_Approval_requested_on',
            'Job Approval requested on',
          )} ${formatDateTime(jobDetails?.updatedOn, language)} ${
            user ? `${t('by', 'by')} ${user.fullName}` : ''
          }`}
        </BodyMedium>
      </div>
    );
  };

  return (
    <Panel
      anchor="right"
      isOpen={isOpen}
      onClose={() => handleMoveBack(false)}
      size="large"
      tapOutsideToClose
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={closePanelHandler} size="medium">
        <SubTitleLarge>
          {isRejected || isApproved
            ? t('Review_Job_Approval', 'Review Job Approval')
            : `${t('Approve', 'Approve')} ${
                jobDetails && jobDetails.title
                  ? jobDetails.title ?? ''
                  : t('Job', 'Job')
              }`}
        </SubTitleLarge>
      </PanelHeader>
      <PanelBody className={css.panelBody}>
        <div className={css.reviewWrapper}>
          {isLoadingJobData ? (
            <div className={css.loader}>
              <CircularLoader />
            </div>
          ) : !isEmpty(jobDetails) ? (
            <>
              <div className={css.tabListWrapper}>
                <TabList
                  // @ts-expect-error - TS2769 - No overload matches this call.
                  onSelect={setSelectedTabOption}
                  selectedTab={selectedOption}
                  size="medium"
                >
                  <Tab
                    label={t('Job_Details', 'Job Details')}
                    tabId="jobDetails"
                  />
                  <Tab
                    label={t('Job_Description', 'Job Description')}
                    tabId="jobDescription"
                  />
                  {!isEmpty(jobApprovalObject) ? (
                    <Tab
                      label={t('Approval_Status', 'Approval Status')}
                      tabId="approvalStatus"
                    />
                  ) : null}
                </TabList>
              </div>
              {(selectedOption.tabId === 'approvalStatus' ||
                selectedOption.tabId === 'jobDetails') &&
                renderStatusAlert()}
              {jobDetails &&
                (selectedOption.tabId === 'approvalStatus' ? (
                  // @ts-expect-error - TS2741 - Property 'type' is missing in type '{ approvalObject: {}; approverStatusesList: never[]; }' but required in type '{ approvalObject: any; approverStatusesList: any; type: any; viewOnly?: boolean | undefined; }'.
                  <ApprovalStatus
                    approvalObject={jobApprovalObject}
                    approverStatusesList={approverStatusesList}
                  />
                ) : selectedOption.tabId === 'jobDetails' ? (
                  <JobDetailsTab jobDetails={jobDetails} />
                ) : selectedOption.tabId === 'jobDescription' ? (
                  <JobDescription jobInfo={jobDetails} />
                ) : null)}
              {modalState === 'approve' && (
                <ApproveModal
                  message={jobApprovalMessage}
                  setMessage={setJobApprovalMessage}
                  onConfirm={() =>
                    handleApprovalAction('APPROVED', {
                      successMessage: `${t(
                        'Job_Approved_successfully',
                        'Job Approved successfully',
                      )}!`,
                      errorMessage: `${t(
                        'Unable_to_approve_job',
                        'Unable to approve job',
                      )}`,
                    })
                  }
                  onClose={() => setModalState(null)}
                  isLoading={approvingJob}
                  t={t}
                />
              )}
              {modalState === 'reject' && (
                <RejectModal
                  message={jobApprovalMessage}
                  setMessage={setJobApprovalMessage}
                  onConfirm={() =>
                    handleApprovalAction('REJECTED', {
                      successMessage: `${t(
                        'Job_Rejected_successfully',
                        'Job Rejected successfully',
                      )}!`,
                      errorMessage: `${t(
                        'Unable_to_reject_job',
                        'Unable to reject job',
                      )}`,
                    })
                  }
                  onClose={() => setModalState(null)}
                  isLoading={approvingJob}
                  disabled={!jobApprovalMessage}
                  t={t}
                />
              )}
            </>
          ) : null}
        </div>
      </PanelBody>

      {renderFooter()}
    </Panel>
  );
};

const ApproveModal = ({
  // @ts-expect-error - TS7031 - Binding element 'message' implicitly has an 'any' type.
  message,
  // @ts-expect-error - TS7031 - Binding element 'setMessage' implicitly has an 'any' type.
  setMessage,
  // @ts-expect-error - TS7031 - Binding element 'onConfirm' implicitly has an 'any' type.
  onConfirm,
  // @ts-expect-error - TS7031 - Binding element 'onClose' implicitly has an 'any' type.
  onClose,
  // @ts-expect-error - TS7031 - Binding element 'isLoading' implicitly has an 'any' type.
  isLoading,
  // @ts-expect-error - TS7031 - Binding element 't' implicitly has an 'any' type.
  t,
}) => (
  <ApprovalModal
    title={t('Confirm_Approval', 'Confirm Approval')}
    message={message}
    setMessage={setMessage}
    onConfirm={onConfirm}
    onClose={onClose}
    isLoading={isLoading}
    confirmText={t('Approve', 'Approve')}
    t={t}
  />
);

const RejectModal = ({
  // @ts-expect-error - TS7031 - Binding element 'message' implicitly has an 'any' type.
  message,
  // @ts-expect-error - TS7031 - Binding element 'setMessage' implicitly has an 'any' type.
  setMessage,
  // @ts-expect-error - TS7031 - Binding element 'onConfirm' implicitly has an 'any' type.
  onConfirm,
  // @ts-expect-error - TS7031 - Binding element 'onClose' implicitly has an 'any' type.
  onClose,
  // @ts-expect-error - TS7031 - Binding element 'isLoading' implicitly has an 'any' type.
  isLoading,
  // @ts-expect-error - TS7031 - Binding element 'disabled' implicitly has an 'any' type.
  disabled,
  // @ts-expect-error - TS7031 - Binding element 't' implicitly has an 'any' type.
  t,
}) => (
  <ApprovalModal
    title={t('Reject_Approval', 'Reject Approval')}
    message={message}
    setMessage={setMessage}
    onConfirm={onConfirm}
    onClose={onClose}
    isLoading={isLoading}
    confirmText={t('Reject', 'Reject')}
    required
    disabled={disabled}
    t={t}
  />
);

const ApprovalModal = ({
  // @ts-expect-error - TS7031 - Binding element 'title' implicitly has an 'any' type.
  title,
  // @ts-expect-error - TS7031 - Binding element 'message' implicitly has an 'any' type.
  message,
  // @ts-expect-error - TS7031 - Binding element 'setMessage' implicitly has an 'any' type.
  setMessage,
  // @ts-expect-error - TS7031 - Binding element 'onConfirm' implicitly has an 'any' type.
  onConfirm,
  // @ts-expect-error - TS7031 - Binding element 'onClose' implicitly has an 'any' type.
  onClose,
  // @ts-expect-error - TS7031 - Binding element 'isLoading' implicitly has an 'any' type.
  isLoading,
  // @ts-expect-error - TS7031 - Binding element 'confirmText' implicitly has an 'any' type.
  confirmText,
  required = false,
  disabled = false,
  // @ts-expect-error - TS7031 - Binding element 't' implicitly has an 'any' type.
  t,
}) => (
  <Modal
    initialFocus={1}
    onClose={onClose}
    isOpen
    classNames={{content: css.approvalModalContainer}}
  >
    <ModalHeader onCloseButtonClick={onClose}>
      <SubTitleLarge>{title}</SubTitleLarge>
    </ModalHeader>
    <ModalBody>
      <Textarea
        label={
          required
            ? t('Add_Reason', 'Add Reason')
            : t('Add_Comment_Optional', 'Add Comment (Optional)')
        }
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        required={required}
      />
    </ModalBody>
    <ModalFooter>
      <Button onClick={onClose} type="ghost">
        {t('Close', 'Close')}
      </Button>
      <Button onClick={onConfirm} isLoading={isLoading} disabled={disabled}>
        {confirmText}
      </Button>
    </ModalFooter>
  </Modal>
);
